/**
 * MedicationList.jsx
 * Shows all medications with:
 * - Name + Dosage
 * - Frequency
 * - Times
 * - Weekly Days
 * - PRN indicator
 * - Edit / Delete buttons
 */

import React from "react";
import {
    Box,
    Paper,
    Typography,
    Divider,
    IconButton,
    Stack,
    Chip
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export default function MedicationList({ meds, onEdit, onDelete }) {
    if (!meds || meds.length === 0) {
        return <Typography>No medications added yet.</Typography>;
    }

    return (
        <Box>
            {meds.map((med) => {
                const s = med.schedule || {};

                return (
                    <Paper key={med.id} sx={{ p: 2, mb: 2 }}>
                        {/* Header row: name + action buttons */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {med.name} {med.dosage ? `(${med.dosage})` : ""}
                            </Typography>

                            <Stack direction="row" spacing={1}>
                                <IconButton onClick={() => onEdit(med)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => onDelete(med)} color="warning">
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {/* PRN Medication */}
                        {s.prn && (
                            <Chip
                                label="As Needed (PRN)"
                                color="error"
                                size="small"
                                sx={{ mt: 1 }}
                            />
                        )}

                        {/* Frequency */}
                        {!s.prn && (
                            <Typography sx={{ mt: 1 }}>
                                <strong>Frequency:</strong> {s.frequency}
                            </Typography>
                        )}

                        {/* Times */}
                        {!s.prn && s.times?.length > 0 && (
                            <Typography>
                                <strong>Times:</strong> {s.times.join(", ")}
                            </Typography>
                        )}

                        {/* Weekly days */}
                        {!s.prn && s.frequency === "Weekly" && s.daysOfWeek?.length > 0 && (
                            <Typography>
                                <strong>Days:</strong> {s.daysOfWeek.join(", ")}
                            </Typography>
                        )}

                        {med.diagnosis && (
                            <Typography sx={{ mt: 1 }}>
                                <strong>Diagnosis:</strong> {med.diagnosis}
                            </Typography>
                        )}

                        {med.sideEffects && (
                            <Typography>
                                <strong>Side Effects:</strong> {med.sideEffects}
                            </Typography>
                        )}

                        {med.reason && (
                            <Typography>
                                <strong>Reason:</strong> {med.reason}
                            </Typography>
                        )}

                        <Divider sx={{ mt: 2 }} />

                        {/* Dates */}
                        <Typography sx={{ mt: 1, fontSize: "0.9rem" }}>
                            <strong>Start:</strong> {med.startDate || "-"}
                        </Typography>

                        <Typography sx={{ fontSize: "0.9rem" }}>
                            <strong>End:</strong> {med.endDate || "No End Date"}
                        </Typography>

                        <Typography sx={{ fontSize: "0.9rem" }}>
                            <strong>Expiry:</strong> {med.expiryDate || "-"}
                        </Typography>
                    </Paper>
                );
            })}
        </Box>
    );
}
